import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import Button from "../../atoms/Button";
import AccountDeletionPublicLayout from "./PublicLayout";
import RecoveryStatusPanel, { isKnownRecoveryStatus } from "./RecoveryStatusPanel";
import AccountDeletionBillingImpactNotice from "../../molecules/AccountDeletionBillingImpactNotice";
import {
    accountDeletionService,
    createAccountDeletionIdempotencyKey,
} from "../../../services/accountDeletionService";
import {
    ACCOUNT_DELETION_SSO_FLOWS,
    readAccountDeletionSsoCallback,
} from "../../../services/accountDeletionSsoCallback";
import {
    getAccountDeletionActionGrant,
    getAccountDeletionCsrfToken,
} from "../../../services/accountDeletionResponseValidation";
import { submitAccountDeletion } from "../../../services/accountDeletionSubmission";
import { requireAccountDeletionAnonymousSession } from "../../../services/accountDeletionAnonymousSession";
import { useAccountDeletionRecoverySession } from "../../../hooks/useAccountDeletionRecoverySession";
import { useSensitivePublicPage } from "../../../hooks/useSensitivePublicPage";
import { useClearSensitiveRefsOnPageHide } from "../../../hooks/useClearSensitiveRefsOnPageHide";
import { recoveryErrorKey } from "./recoveryUi";

const PROCESSING_VIEW = "processing";
const DELETION_VIEW = "deletion";
const RECOVERY_VIEW = "recovery";
const ERROR_VIEW = "error";

const AccountDeletionSsoCallbackPage = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    useSensitivePublicPage(t("accountDeletionPublic.authorize.documentTitle"));
    const callbackRef = useRef(undefined);
    if (callbackRef.current === undefined) {
        callbackRef.current = readAccountDeletionSsoCallback();
    }
    const csrfTokenRef = useRef(null);
    const reauthTokenRef = useRef(null);
    const idempotencyKeyRef = useRef(null);
    const deletionFlowRef = useRef(null);
    const finalRegionRef = useRef(null);
    const recoveryRegionRef = useRef(null);
    const recoverySession = useAccountDeletionRecoverySession();
    const { acceptCredentials: acceptRecoveryCredentials } = recoverySession;
    useClearSensitiveRefsOnPageHide(
        callbackRef,
        csrfTokenRef,
        reauthTokenRef,
        idempotencyKeyRef,
        deletionFlowRef,
    );
    const [view, setView] = useState(PROCESSING_VIEW);
    const [errorKey, setErrorKey] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(true);

    useEffect(() => {
        const callback = callbackRef.current;
        callbackRef.current = null;
        if (!callback
            || callback.providerError
            || !callback.code
            || !callback.state
            || !Object.values(ACCOUNT_DELETION_SSO_FLOWS).includes(callback.flow)) {
            setErrorKey("accountDeletionPublic.authorize.errors.generic");
            setView(ERROR_VIEW);
            setIsSubmitting(false);
            return undefined;
        }

        let active = true;
        const exchange = async () => {
            if (callback.flow !== ACCOUNT_DELETION_SSO_FLOWS.active) {
                // A different tab or flow may have recreated ambient HttpOnly
                // auth/SSO cookies while the browser was at the provider.
                await requireAccountDeletionAnonymousSession(dispatch);
                if (!active) {
                    return;
                }
            }

            if (callback.flow === ACCOUNT_DELETION_SSO_FLOWS.recovery) {
                const response = await accountDeletionService.exchangeRecoverySso({
                    provider: callback.provider,
                    state: callback.state,
                    code: callback.code,
                });
                if (!active) {
                    return;
                }
                setView(RECOVERY_VIEW);
                try {
                    await acceptRecoveryCredentials(response);
                } catch (error) {
                    if (active) {
                        setErrorKey(recoveryErrorKey(error));
                    }
                }
                return;
            }

            let response;
            if (callback.flow === ACCOUNT_DELETION_SSO_FLOWS.active) {
                const csrfResponse = await accountDeletionService.getCsrfToken();
                const csrfToken = getAccountDeletionCsrfToken(csrfResponse);
                if (!csrfToken) {
                    throw new TypeError("Account-deletion CSRF token is unavailable");
                }
                csrfTokenRef.current = csrfToken;
                response = await accountDeletionService.exchangeSsoAuthorization({
                    provider: callback.provider,
                    state: callback.state,
                    code: callback.code,
                    confirmation: "DELETE",
                    csrfToken,
                });
            } else {
                response = await accountDeletionService.exchangeRestrictedSsoAuthorization({
                    provider: callback.provider,
                    state: callback.state,
                    code: callback.code,
                    confirmation: "DELETE",
                });
            }

            if (!active) {
                return;
            }
            const reauthToken = getAccountDeletionActionGrant(response);
            if (!reauthToken) {
                throw new TypeError("Account-deletion action grant is unavailable");
            }
            reauthTokenRef.current = reauthToken;
            idempotencyKeyRef.current = createAccountDeletionIdempotencyKey();
            deletionFlowRef.current = callback.flow;
            setView(DELETION_VIEW);
        };

        exchange()
            .catch((error) => {
                if (active) {
                    setErrorKey(callback.flow === ACCOUNT_DELETION_SSO_FLOWS.recovery
                        ? recoveryErrorKey(error)
                        : "accountDeletionPublic.authorize.errors.generic");
                    setView(ERROR_VIEW);
                }
            })
            .finally(() => {
                if (active) {
                    setIsSubmitting(false);
                }
            });

        return () => {
            active = false;
        };
    }, [acceptRecoveryCredentials, dispatch]);

    useEffect(() => {
        const target = view === DELETION_VIEW
            ? finalRegionRef.current
            : view === RECOVERY_VIEW
                ? recoveryRegionRef.current
                : null;
        try {
            target?.focus({ preventScroll: true });
        } catch (_) {
            target?.focus();
        }
    }, [view]);

    const createDeletion = async () => {
        if (!reauthTokenRef.current
            || !idempotencyKeyRef.current
            || !deletionFlowRef.current) {
            setErrorKey("accountDeletionPublic.authorize.errors.expired");
            setView(ERROR_VIEW);
            return;
        }
        setIsSubmitting(true);
        setErrorKey(null);
        try {
            await submitAccountDeletion({
                csrfToken: deletionFlowRef.current === ACCOUNT_DELETION_SSO_FLOWS.active
                    ? csrfTokenRef.current
                    : undefined,
                reauthToken: reauthTokenRef.current,
                idempotencyKey: idempotencyKeyRef.current,
                dispatch,
            });
            csrfTokenRef.current = null;
            reauthTokenRef.current = null;
            idempotencyKeyRef.current = null;
            deletionFlowRef.current = null;
        } catch (error) {
            setErrorKey("accountDeletionPublic.authorize.errors.generic");
            if (error?.code === "REAUTHENTICATION_REQUIRED"
                || error?.code === "IDEMPOTENCY_CONFLICT") {
                csrfTokenRef.current = null;
                reauthTokenRef.current = null;
                idempotencyKeyRef.current = null;
                deletionFlowRef.current = null;
                setView(ERROR_VIEW);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const refreshRecoveryStatus = async () => {
        setIsSubmitting(true);
        setErrorKey(null);
        try {
            await recoverySession.refreshStatus();
        } catch (error) {
            setErrorKey(recoveryErrorKey(error));
        } finally {
            setIsSubmitting(false);
        }
    };

    const confirmRecovery = async () => {
        setIsSubmitting(true);
        setErrorKey(null);
        try {
            const nextStatus = await recoverySession.confirmRecovery();
            if (!isKnownRecoveryStatus(nextStatus?.status)) {
                setErrorKey("accountDeletionPublic.recovery.errors.generic");
            }
        } catch (error) {
            setErrorKey(recoveryErrorKey(error));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <AccountDeletionPublicLayout
            title={t("accountDeletionPublic.authorize.title")}
            description={t("accountDeletionPublic.authorize.description")}
            showRecoveryLink
            showRestrictedLink
        >
            {errorKey && (
                <div className="account-deletion-public-card__feedback account-deletion-public-card__feedback--error" role="alert">
                    {t(errorKey)}
                </div>
            )}

            {view === PROCESSING_VIEW && (
                <div className="account-deletion-public-card__feedback" role="status" aria-live="polite">
                    {t("accountDeletionPublic.authorize.processing")}
                </div>
            )}

            {view === DELETION_VIEW && (
                <div
                    ref={finalRegionRef}
                    className="account-deletion-public-card__status"
                    role="status"
                    aria-live="polite"
                    tabIndex={-1}
                >
                    <p>{t("accountDeletionPublic.authorize.finalWarning")}</p>
                    <AccountDeletionBillingImpactNotice nonEnumerating />
                    <Button variant="danger" onClick={createDeletion} disabled={isSubmitting}>
                        {isSubmitting
                            ? t("accountDeletionPublic.authorize.actions.deleting")
                            : t("accountDeletionPublic.authorize.actions.delete")}
                    </Button>
                </div>
            )}

            {view === RECOVERY_VIEW && (
                <RecoveryStatusPanel
                    status={recoverySession.status}
                    hasConfirmation={recoverySession.hasConfirmation}
                    isSubmitting={isSubmitting}
                    onConfirm={confirmRecovery}
                    onRefresh={refreshRecoveryStatus}
                    lastCheckedAt={recoverySession.lastCheckedAt}
                    panelRef={recoveryRegionRef}
                />
            )}

            {view === ERROR_VIEW && (
                <p>{t("accountDeletionPublic.authorize.restart")}</p>
            )}
        </AccountDeletionPublicLayout>
    );
};

export default AccountDeletionSsoCallbackPage;
